import React, { useState, useEffect, useRef } from 'react';
import { Mail, Copy, Zap, User as UserIcon, CheckCircle2, Loader2, Globe } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Place, EmailData, UnlockEstimateResponse } from '../../../types/results.types';
import Avatar from '../../common/Avatar';
import DMUnlockButton from '../DMUnlockButton/DMUnlockButton';
import BulkDMUnlockModal from '../BulkDMUnlockModal/BulkDMUnlockModal';
import { toTitleCase } from '../../../utils/formatting';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useAppSelector, useAppDispatch } from '../../../hooks/reduxHooks';
import { fetchResults } from '../../../store/slices/resultsSlice';
import { selectIsBulkDMPolling } from '../../../store/slices/dmUnlockSlice';
import { dmUnlockApi } from '../../../api/dmUnlock';
import { balanceApi } from '../../../api/balance';
import EmailBadge, { CatchAllBadge } from '../EmailBadge/EmailBadge';
import { getEmailProvenance, getPersonProvenance } from '../../../utils/resultProvenance';
import { findablePeopleIds } from '../../../utils/rowActions';

interface PeopleTabProps {
  place: Place;
  searchId?: number;
  onUpgradeClick?: () => void;
  currentPlan?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
}

const AUTO_NO_EMAIL_REASON_LABELS: Record<string, string> = {
  FAILED_NO_WEBSITE: 'no website found',
  FAILED_NO_PATTERNS: 'email format unknown',
  CATCH_ALL: 'could not confirm',
};

const PeopleTab: React.FC<PeopleTabProps> = ({
  place,
  searchId,
  onUpgradeClick,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);
  const [prefetchedEstimate, setPrefetchedEstimate] = useState<UnlockEstimateResponse | null>(null);
  const [prefetchedBalance, setPrefetchedBalance] = useState<number | null>(null);
  const [prefetchError, setPrefetchError] = useState<string | null>(null);
  const [expandedEmailPersonIds, setExpandedEmailPersonIds] = useState<Set<number>>(new Set());
  const [noEmailFoundIds, setNoEmailFoundIds] = useState<Set<number>>(new Set());
  const { hasDMUnlockAccess, hasConfidenceBadgesAccess } = useFeatureAccess();
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const isDMUnlockPolling = useAppSelector(selectIsBulkDMPolling);
  const { pagination, filters } = useAppSelector((state) => state.results);

  const recentCompletionsCount = useAppSelector((state) => state.dmUnlock.recentCompletions.length);
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevCountRef = useRef(0);
  const gatedViewedRef = useRef(false);
  const allPeople = place.people ?? [];
  const gatedPeople = allPeople.filter(p => p.email_access_state === 'gated_upgrade');
  const findablePersonIdSet = new Set(findablePeopleIds(place));
  const autoCheckedPeople = allPeople.filter(
    p => p.email_access_state === 'auto_no_email' || p.email_access_state === 'auto_found'
  );
  const isDiscover = (currentPlan || '').toLowerCase() === 'discover';
  const gateReason = place.email_gate_reason || 'discover_remaining_lead';
  const autoFindEventProps = {
    auto_find_mode: autoFindMode ?? 'discover_auto',
    success_limit: autoFindSuccessLimit ?? 0,
    success_count: autoFindSuccessCount ?? 0,
    attempt_count: autoFindAttemptCount ?? 0,
    no_email_count: autoFindNoEmailCount ?? 0,
    catch_all_count: autoFindCatchAllCount ?? 0,
    gate_reason: autoFindGuardrailReason ?? gateReason,
    purchased_dm_spent: 0,
  };
  const peopleToFind = allPeople.filter(p => findablePersonIdSet.has(p.id));

  useEffect(() => {
    if (recentCompletionsCount > prevCountRef.current && searchId) {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      fetchDebounceRef.current = setTimeout(() => {
        dispatch(fetchResults({
          searchId,
          page: pagination.page,
          limit: pagination.itemsPerPage,
          filters,
          silent: true,
        }));
      }, 1500);
    }
    prevCountRef.current = recentCompletionsCount;
  }, [recentCompletionsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gatedViewedRef.current || gatedPeople.length === 0) return;
    gatedViewedRef.current = true;
    posthog?.capture('gated_email_action_viewed', {
      search_id: searchId,
      current_plan: 'discover',
      surface: 'company_drawer_people_tab',
      ...autoFindEventProps,
    });
  }, [
    autoFindAttemptCount,
    autoFindCatchAllCount,
    autoFindGuardrailReason,
    autoFindMode,
    autoFindNoEmailCount,
    autoFindSuccessCount,
    autoFindSuccessLimit,
    gatedPeople.length,
    gateReason,
    posthog,
    searchId,
  ]);

  const handlePersonUnlockSuccess = () => {
    if (searchId) {
      dispatch(fetchResults({
        searchId,
        page: pagination.page,
        limit: pagination.itemsPerPage,
        filters,
        silent: true,
      }));
    }
  };

  const handleFindEmails = async () => {
    setPrefetchError(null);
    setIsLoadingEstimate(true);
    try {
      const personIds = peopleToFind.map(p => p.id);
      const [estimateResponse, balance] = await Promise.all([
        dmUnlockApi.estimateCost({ person_ids: personIds }),
        balanceApi.getBalance(),
      ]);
      setPrefetchedEstimate(estimateResponse);
      setPrefetchedBalance(balance.dm_unlocks_remaining);
      setIsBulkModalOpen(true);
    } catch {
      setPrefetchError('Failed to load estimate. Please try again.');
    } finally {
      setIsLoadingEstimate(false);
    }
  };

  const toggleEmailExpanded = (personId: number) => {
    setExpandedEmailPersonIds(prev => {
      const next = new Set(prev);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  };

  if (!place.people || place.people.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <UserIcon className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm font-semibold text-gray-500">No key people found</p>
        <p className="text-xs text-gray-400 mt-1">No decision makers were identified for this business.</p>
      </div>
    );
  }

  const discoveredCount = place.people.flatMap(p => p.emails || []).filter(
    e => e.verification_status === 'DISCOVERED' && !e.is_catch_all
  ).length;

  const withEmail = place.people.filter(p => p.emails && p.emails.length > 0 && p.unlock_type !== 'FREE_NO_EMAIL').length;
  const withoutEmail = place.people.length - withEmail;

  const handleCopy = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 1800);
    } catch {}
  };

  const visiblePeople = place.people.slice(0, expanded ? place.people.length : 3);

  return (
    <div className="space-y-3">
      {/* Recommended next step banner */}
      {gatedPeople.length > 0 && peopleToFind.length === 0 ? (
        <div className="flex items-center gap-4 rounded-xl border border-[#F0DFDF] bg-[#FFF9F7] px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#B46A6D]">Decision-makers identified</p>
            <p className="text-sm font-bold text-[#2D3142] leading-snug">
              {autoCheckedPeople.length > 0
                ? `We checked ${autoCheckedPeople.length} ${autoCheckedPeople.length === 1 ? 'person' : 'people'}. ${gatedPeople.length} ${gatedPeople.length === 1 ? 'person remains' : 'people remain'} in this search.`
                : 'Upgrade to find their emails and turn this lead into a contact.'}
            </p>
          </div>
          <button
            onClick={() => {
              posthog?.capture('gated_email_action_clicked', {
                search_id: searchId,
                current_plan: 'discover',
                surface: 'company_drawer_people_tab_banner',
                ...autoFindEventProps,
              });
              onUpgradeClick?.();
            }}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#E6D7D7] bg-white px-3 py-1 text-xs font-semibold text-[#B46A6D] hover:border-[#C98B8E] hover:bg-[#FBF7F7]"
          >
            Upgrade
          </button>
        </div>
      ) : peopleToFind.length > 0 && (
        <div className="flex items-center gap-4 bg-[#2D3142] rounded-xl px-4 py-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Recommended next step</p>
            <p className="text-sm font-bold text-white leading-snug">
              Find emails for {peopleToFind.length} {peopleToFind.length === 1 ? 'person' : 'people'}
            </p>
            {discoveredCount > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                Then verify the {discoveredCount} discovered before sending
              </p>
            )}
          </div>
          <button
            onClick={handleFindEmails}
            disabled={isDMUnlockPolling || !hasDMUnlockAccess || isLoadingEstimate}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#2D3142] text-xs font-bold rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingEstimate
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading...</>
              : 'Find emails →'}
          </button>
        </div>
      )}

      {/* Section header */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Identified people {place.people.length}
        </span>
        <span className="text-xs text-gray-400">
          {withEmail} with email · {withoutEmail} without
        </span>
      </div>

      {/* Person cards */}
      {visiblePeople.map(person => {
        const personProvenance = getPersonProvenance(person.source_url);
        const emails = (person.emails ?? []).filter(
          e => !noEmailFoundIds.has(person.id) && person.unlock_type !== 'FREE_NO_EMAIL'
        );
        const hasEmails = emails.length > 0;
        const EMAIL_PREVIEW_LIMIT = 2;
        const isEmailsExpanded = expandedEmailPersonIds.has(person.id);
        const visibleEmails = hasEmails
          ? (isEmailsExpanded ? emails : emails.slice(0, EMAIL_PREVIEW_LIMIT))
          : [];
        const hiddenEmailCount = emails.length - EMAIL_PREVIEW_LIMIT;
        const personName = toTitleCase(`${person.corrected_first_name || person.first_name} ${person.corrected_last_name || person.last_name}`);

        return (
          <div
            key={person.id}
            className={`bg-white rounded-xl p-4 ${
              hasEmails
                ? 'border border-[#F9F5F5] shadow-sm'
                : 'border border-dashed border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <Avatar
                variant="person"
                firstName={person.corrected_first_name || person.first_name}
                lastName={person.corrected_last_name || person.last_name}
                size="md"
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#2D3142] text-sm leading-tight">
                  {personName}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {toTitleCase(person.corrected_role || person.role)}
                </p>
                {personProvenance && (
                  <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                    <span className="inline-flex flex-shrink-0 items-center gap-1.5 text-[#8A3033]">
                      <Globe className="w-3.5 h-3.5" />
                      Identified on {personProvenance.sourceName}
                    </span>
                    {personProvenance.sourceUrl && personProvenance.sourceLabel && (
                      <>
                        <span>·</span>
                        <a
                          href={personProvenance.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate underline underline-offset-2 text-[#8A3033] hover:text-[#722629]"
                        >
                          {personProvenance.sourceLabel}
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {hasEmails ? (
              <div className="space-y-1.5 border-t border-[#F9F5F5] pt-3">
                {visibleEmails.map((email: EmailData) => {
                  const provenance = getEmailProvenance(email);

                  return (
                    <div key={email.id ?? email.email}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-mono text-black min-w-0 truncate flex-1">
                          {email.email}
                        </span>
                        {email.is_catch_all
                          ? <CatchAllBadge />
                          : hasConfidenceBadgesAccess && <EmailBadge email={email} />}
                        <button
                          onClick={() => handleCopy(email.email)}
                          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-[#8A3033] transition-colors"
                          title="Copy email"
                        >
                          {copiedEmail === email.email
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            : <Copy className="w-3.5 h-3.5" />
                          }
                        </button>
                        {searchId && !email.is_catch_all && email.verification_status !== 'GUARANTEED' && (
                          <DMUnlockButton
                            personId={person.id}
                            personName={personName}
                            searchId={searchId}
                            place={place}
                            hasDiscoveredEmail={true}
                            onSuccess={(response) => {
                              if (!response.email) setNoEmailFoundIds(prev => new Set([...prev, person.id]));
                              handlePersonUnlockSuccess();
                            }}
                            onBalanceUpdate={() => {}}
                            onUpgradeClick={onUpgradeClick}
                          />
                        )}
                      </div>
                      {provenance && (
                        <div className="ml-5 mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400">
                          <span className="text-[#8A3033]">{provenance.label}</span>
                          {provenance.sourceUrl && provenance.sourceLabel && (
                            <>
                              <span>·</span>
                              <a
                                href={provenance.sourceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate underline underline-offset-2 text-[#8A3033] hover:text-[#722629]"
                              >
                                {provenance.sourceLabel}
                              </a>
                            </>
                          )}
                        </div>
                      )}
                      {email.is_catch_all && (
                        <p className="text-[11px] text-amber-600 mt-1.5 ml-5">Domain accepts any address. Existence not confirmed.</p>
                      )}
                      {person.email_access_state === 'auto_found' && (
                        <p className="text-[10px] text-gray-400 mt-1 ml-5">auto-found</p>
                      )}
                    </div>
                  );
                })}

                {emails.length > EMAIL_PREVIEW_LIMIT && (
                  <button
                    onClick={() => toggleEmailExpanded(person.id)}
                    className="text-[11px] text-[#8A3033] hover:underline ml-5"
                  >
                    {isEmailsExpanded
                      ? 'Show less'
                      : `Show ${hiddenEmailCount} more email${hiddenEmailCount > 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            ) : person.email_access_state === 'finding' ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 border-t border-[#F9F5F5] pt-3">
                <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
                <span>Looking...</span>
              </div>
            ) : person.email_access_state === 'gated_upgrade' ? (
              <div className="flex items-center justify-between gap-2 border-t border-[#F9F5F5] pt-3">
                <span className="text-xs text-gray-400 italic">Not checked in this search yet</span>
                <button
                  type="button"
                  onClick={() => {
                    posthog?.capture('gated_email_action_clicked', {
                      search_id: searchId,
                      current_plan: 'discover',
                      surface: 'company_drawer_people_tab',
                      ...autoFindEventProps,
                    });
                    onUpgradeClick?.();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E6D7D7] bg-white px-3 py-1 text-xs font-semibold text-[#B46A6D] hover:border-[#C98B8E] hover:bg-[#FBF7F7]"
                >
                  <span>Find email</span>
                  <span>· Upgrade</span>
                </button>
              </div>
            ) : person.is_unlocked || person.email_access_state === 'auto_no_email' ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 border-t border-[#F9F5F5] pt-3">
                {person.email_access_state === 'auto_no_email' ? (
                  <span
                    className="inline-flex w-fit flex-col text-xs leading-tight text-[#9CA3AF]"
                    title={
                      person.unlock_type === 'FAILED_NO_WEBSITE'
                        ? 'This business has no website we could check. We did not charge you.'
                        : person.unlock_type === 'FAILED_NO_PATTERNS'
                          ? 'We found the website but could not work out their email format. You were not charged.'
                          : person.unlock_type === 'CATCH_ALL'
                            ? 'This domain accepts any address, so we could not confirm a specific one. You were not charged.'
                            : 'We looked but could not find a confirmed email. You were not charged.'
                    }
                  >
                    <span className="font-medium">
                      {person.unlock_type
                        ? (AUTO_NO_EMAIL_REASON_LABELS[person.unlock_type] ?? 'no email found')
                        : 'no email found'}
                    </span>
                  </span>
                ) : (
                  <span className="font-medium text-[#9CA3AF]">no email found</span>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 border-t border-[#F9F5F5] pt-3">
                <span className="text-xs text-gray-400 italic">
                  {isDiscover ? 'Not checked in this search yet' : 'No email on file'}
                </span>
                <DMUnlockButton
                  personId={person.id}
                  personName={personName}
                  searchId={searchId}
                  place={place}
                  onSuccess={handlePersonUnlockSuccess}
                  onBalanceUpdate={() => {}}
                  compact
                  onUpgradeClick={onUpgradeClick}
                />
              </div>
            )}
          </div>
        );
      })}

      {place.people.length > 3 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full py-2 text-sm font-bold text-[#8A3033] hover:text-[#722629] transition-colors duration-200"
        >
          {expanded ? 'Show less' : `Show ${place.people.length - 3} more`}
        </button>
      )}

      {prefetchError && (
        <p className="text-xs text-red-600 mt-1">{prefetchError}</p>
      )}

      {isBulkModalOpen && (
        <BulkDMUnlockModal
          isOpen={isBulkModalOpen}
          onClose={() => {
            setIsBulkModalOpen(false);
            setPrefetchedEstimate(null);
            setPrefetchedBalance(null);
          }}
          personIds={peopleToFind.map(p => p.id)}
          searchId={searchId}
          initialEstimate={prefetchedEstimate ?? undefined}
          initialDmUnlocksRemaining={prefetchedBalance ?? undefined}
          onUpgradeClick={onUpgradeClick}
          onSuccess={() => setIsBulkModalOpen(false)}
        />
      )}
    </div>
  );
};

export default PeopleTab;
